import { useEffect, useRef } from 'react';
import {
  ANALYZING_COPY,
  Icons,
  InferenceFreshness,
  NotificationDot,
  RiskBadge,
} from './ui-atoms.jsx';
import { severityToRiskTier } from '../../shared/risk-tiers.js';
import { MeterPanel } from './meter-panel.jsx';
const RISK_AXIS_LABELS = Object.freeze({
  PlaceOfBirth: 'Place of Birth',
  MaritalStatus: 'Marital Status',
  EducationLevel: 'Education',
});

export function riskAxisLabel(axis) {
  return RISK_AXIS_LABELS[axis] ?? axis;
}

export function meanRisk(meter, valueKey) {
  if (!Array.isArray(meter) || meter.length === 0) return 0;
  return meter.reduce((total, row) => total + row[valueKey], 0) / meter.length;
}

// showDot: reveal notification (see NotificationDot) — true only while this
// arm's timed reveal delay was configured and the participant hasn't opened
// the meter yet. Defaults false so every existing caller (researcher demo,
// revealDelayMs=0 arms) renders exactly as before.
export function RiskBar({
  meter,
  onExpand,
  showDot = false,
  unavailable = false,
  pending = false,
  disabled = false,
  className,
  inferenceTriggerTime = null,
}) {
  // Compact: label + value pairs for Me vs Them. No magnitude bars.
  // The fixed D5 risk badge carries the level.
  // Values are risk 0..1 (1 = highest risk / most exposed).
  const meterRow = (value, label) => (
    <div
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 6,
        minWidth: 0,
      }}
    >
      <span className="eyebrow" style={{ color: 'var(--ink-soft)' }}>
        {label}
      </span>
      <RiskBadge level={severityToRiskTier(value)} />
    </div>
  );

  return (
    <button
      className={className}
      onClick={onExpand}
      disabled={disabled}
      aria-label="Open the risk meter"
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '10px 14px',
        background: 'var(--paper)',
        border: 'none',
        borderBottom: '1px solid var(--line)',
        cursor: disabled ? 'default' : 'pointer',
        fontFamily: 'inherit',
        opacity: disabled ? 0.4 : 1,
        textAlign: 'left',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          flexShrink: 0,
          position: 'relative',
        }}
      >
        {Icons.gauge(16, 'var(--indigo)')}
        <span className="eyebrow" style={{ color: 'var(--indigo)', fontWeight: 600 }}>
          RISK
        </span>
        {inferenceTriggerTime && (
          <InferenceFreshness
            triggerTime={inferenceTriggerTime}
            style={{ fontSize: 9, marginLeft: 2 }}
          />
        )}
        {showDot && <NotificationDot />}
      </div>
      {unavailable ? (
        <span role="status" style={{ flex: 1, color: 'var(--ink-soft)', fontSize: 11 }}>
          Risk analysis is unavailable right now.
        </span>
      ) : pending ? (
        <span role="status" style={{ flex: 1, color: 'var(--ink-soft)', fontSize: 11 }}>
          {ANALYZING_COPY}
        </span>
      ) : (
        <>
          {meterRow(meanRisk(meter, 'meValue'), 'You')}
          {meterRow(meanRisk(meter, 'themValue'), 'Them')}
        </>
      )}
      <div style={{ flexShrink: 0, color: 'var(--ink-muted)' }}>
        {Icons.expand(12, 'currentColor')}
      </div>
    </button>
  );
}

// RadarChart
//
// If `themValues` is omitted, the chart renders only the Me polygon.
// Mirror (F1) uses the one-sided form; F3 RiskMeterSheet passes both.
// There is intentionally no me-omitted mode — Mirror is one-sided
// "what they could infer about you", F3 is two-sided "you vs. them".
//
// Values are risk 0..1 (1 = highest risk): a larger polygon = more
// exposed, matching the F4 hotspot's "bigger = riskier" reading.
//
// Inner radius is `size/2 - 64` (was -48) so the longest axis labels
// ("Marital Status", "Place of Birth") stay inside the viewBox at the
// bottom corners. `overflow: visible` on the SVG covers the residual
// few pixels for the worst label at the smallest expected size.
export function RadarChart({
  axes,
  axisLabels,
  meValues,
  themValues,
  size: requestedSize,
  selected,
  onSelectAxis,
}) {
  const isSelectable = selected !== undefined || onSelectAxis !== undefined;
  const size = requestedSize ?? (isSelectable ? 260 : 300);
  const cx = size / 2,
    cy = size / 2;
  const radius = size / 2 - 64;
  const n = axes.length;
  const labels = axisLabels ?? axes.map(riskAxisLabel);
  const angleFor = (i) => -Math.PI / 2 + (i / n) * Math.PI * 2;
  const pointFor = (i, v) => {
    const a = angleFor(i);
    const r = radius * v;
    return [cx + Math.cos(a) * r, cy + Math.sin(a) * r];
  };
  const polygonPath = (values) =>
    values
      .map((v, i) => {
        const [x, y] = pointFor(i, v);
        return `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
      })
      .join(' ') + ' Z';

  // Rings at 0.25, 0.5, 0.75, 1.0
  const rings = [0.25, 0.5, 0.75, 1.0];
  const hasThem = Array.isArray(themValues);
  const selIdx = selected !== undefined ? axes.indexOf(selected) : -1;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ display: 'block', overflow: 'visible' }}
    >
      {/* concentric rings */}
      {rings.map((r, i) => (
        <circle
          key={i}
          cx={cx}
          cy={cy}
          r={radius * r}
          fill="none"
          stroke="var(--line)"
          strokeWidth={i === rings.length - 1 ? 1 : 0.7}
          strokeDasharray={i === rings.length - 1 ? 'none' : '2 3'}
        />
      ))}
      {/* spokes (selected one heavier) */}
      {axes.map((_, i) => {
        const [x, y] = pointFor(i, 1);
        const sel = i === selIdx;
        return (
          <line
            key={i}
            x1={cx}
            y1={cy}
            x2={x}
            y2={y}
            stroke={sel ? 'var(--indigo)' : 'var(--line)'}
            strokeWidth={sel ? 1.6 : 0.7}
          />
        );
      })}

      {/* Them polygon (lower risk — lighter dashed fill) */}
      {hasThem && (
        <path
          d={polygonPath(themValues)}
          fill={isSelectable ? 'oklch(45% 0.11 265 / 0.06)' : 'oklch(45% 0.11 265 / 0.1)'}
          stroke="var(--indigo)"
          strokeWidth={isSelectable ? '1' : '1.4'}
          strokeDasharray="4 3"
          opacity={isSelectable ? '0.7' : undefined}
        />
      )}
      {/* Me polygon (more exposed — orange warm) */}
      <path
        d={polygonPath(meValues)}
        fill={isSelectable ? 'oklch(70% 0.14 30 / 0.12)' : 'oklch(70% 0.14 30 / 0.18)'}
        stroke="oklch(55% 0.14 30)"
        strokeWidth={isSelectable ? '1.2' : '1.6'}
        opacity={isSelectable ? '0.7' : undefined}
      />

      {/* data points */}
      {isSelectable ? (
        axes.map((_, i) => {
          const sel = i === selIdx;
          const [mx, my] = pointFor(i, meValues[i]);
          return (
            <g key={i}>
              {hasThem && (
                <circle
                  cx={pointFor(i, themValues[i])[0]}
                  cy={pointFor(i, themValues[i])[1]}
                  r={sel ? 3.5 : 2}
                  fill="var(--indigo)"
                />
              )}
              <circle cx={mx} cy={my} r={sel ? 4.5 : 2.5} fill="oklch(55% 0.14 30)" />
              {sel && (
                <circle
                  cx={mx}
                  cy={my}
                  r={9}
                  fill="none"
                  stroke="oklch(55% 0.14 30)"
                  strokeWidth="1"
                  opacity="0.5"
                />
              )}
            </g>
          );
        })
      ) : (
        <>
          {hasThem &&
            themValues.map((v, i) => {
              const [x, y] = pointFor(i, v);
              return <circle key={`t${i}`} cx={x} cy={y} r="2.5" fill="var(--indigo)" />;
            })}
          {meValues.map((v, i) => {
            const [x, y] = pointFor(i, v);
            return <circle key={`m${i}`} cx={x} cy={y} r="3" fill="oklch(55% 0.14 30)" />;
          })}
        </>
      )}

      {/* axis labels */}
      {axes.map((axis, i) => {
        const [x, y] = pointFor(i, 1.18);
        const a = angleFor(i);
        // anchor based on angle
        let anchor = 'middle';
        if (Math.cos(a) > 0.2) anchor = 'start';
        else if (Math.cos(a) < -0.2) anchor = 'end';
        const sel = i === selIdx;

        const textEl = (
          <text
            key={isSelectable ? undefined : i}
            x={x}
            y={y}
            textAnchor={anchor}
            dominantBaseline="middle"
            style={{
              fontFamily: 'IBM Plex Mono, monospace',
              fontSize: sel ? 11 : 10,
              fill: sel ? 'var(--indigo)' : 'var(--ink-soft)',
              letterSpacing: '0.04em',
              fontWeight: sel ? 600 : 400,
            }}
          >
            {labels[i]}
          </text>
        );

        if (!isSelectable) {
          return textEl;
        }

        return (
          <g
            key={i}
            style={onSelectAxis ? { cursor: 'pointer' } : undefined}
            onClick={onSelectAxis ? () => onSelectAxis(axis) : undefined}
          >
            {onSelectAxis && (
              <rect x={x - 40} y={y - 10} width={80} height={20} fill="transparent" />
            )}
            {textEl}
          </g>
        );
      })}
    </svg>
  );
}

// RiskMeterSheet
//
// F3 chrome wrapper around MeterPanel. The panel itself (legend, radar,
// scale hint, callout, breakdown) is shared with F1 Mirror; this file
// owns only the sheet shell — overlay, grabber, role=dialog, focus
// trap, eyebrow/title row, close button.
//
// onOpenDrilldown(axis?) is the participant-side path to the per-axis
// drill-down sheet. Pre-T#: the only way to reach drilldown was the
// researcher launcher SubLink — participants had no affordance and
// asked the moderator "how do I see why Location is high?" mid-session.
// Per-axis breakdown rows now invoke onOpenDrilldown(axisKey) so the
// drilldown opens with that axis pre-selected. Caller clears 'meter'
// and opens 'drilldown' atomically, so the selected axis arrives via
// MeterDrilldownSheet's lazy-init path (no prop drilling needed —
// drilldown reads it from local state seeded with the selected axis,
// see app.jsx jumpToAxisDrilldown).
export function RiskMeterSheet({
  meter,
  onClose,
  onOpenDrilldown,
  unavailable = false,
  pending = false,
  inferenceTriggerTime = null,
}) {
  const closeBtnRef = useRef(null);
  // users land inside the sheet instead of tabbing through the chat.
  useEffect(() => {
    const previouslyFocused = typeof document !== 'undefined' ? document.activeElement : null;
    closeBtnRef.current?.focus();
    return () => {
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, []);

  return (
    <>
      <div className="sheet-overlay" onClick={onClose} />
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="risk-sheet-title"
        style={{ maxHeight: '82%' }}
      >
        <div className="sheet-grabber" />

        <div
          style={{
            padding: '14px 20px 8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div className="eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>Risk Meter · F3</span>
              {inferenceTriggerTime && (
                <>
                  <span>·</span>
                  <InferenceFreshness triggerTime={inferenceTriggerTime} />
                </>
              )}
            </div>
            <div id="risk-sheet-title" style={{ fontSize: 19, fontWeight: 600, marginTop: 3 }}>
              How exposed are you?
            </div>
          </div>
          <button
            ref={closeBtnRef}
            onClick={onClose}
            aria-label="Close risk meter"
            style={{
              background: 'var(--paper-2)',
              border: 'none',
              borderRadius: 999,
              width: 32,
              height: 32,
              cursor: 'pointer',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            {Icons.close(16, 'var(--ink-soft)')}
          </button>
        </div>

        <div
          className="scroll"
          style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '6px 16px 28px' }}
        >
          {unavailable ? (
            <div
              role="status"
              style={{ margin: '6px 4px 0', color: 'var(--ink-soft)', fontSize: 12 }}
            >
              Risk Meter analysis is unavailable right now. Your sent messages were not affected.
            </div>
          ) : pending ? (
            <div
              role="status"
              style={{ margin: '6px 4px 0', color: 'var(--ink-soft)', fontSize: 12 }}
            >
              {ANALYZING_COPY}
            </div>
          ) : null}
          {(!pending || unavailable) && (
            <MeterPanel
              meter={meter}
              oneSided={false}
              onOpenDrilldown={onOpenDrilldown}
              size={300}
            />
          )}
        </div>
      </div>
    </>
  );
}
